import React, { useLayoutEffect, useRef, useState } from 'react';
import { webOrigin } from '../../utils/brand';
import { useNavigate } from 'react-router-dom';
import ShareSheet from '../ShareSheet';
import PosterSlot from '../PosterSlot';
import { eventPricing } from '../../utils/eventPricing';
import { recordExternalInterest } from '../../utils/externalInterest';
import { eventService } from '../../services';
import { COLORS } from './wallTokens';
import CoverActions from './CoverActions';
import AvatarStack from '../AvatarStack';
import { cardGeometry } from './wallCardGeometry';
import WallCardSkeleton from './WallCardSkeleton';
import usePosterReveal from './usePosterReveal';
import { eventPath } from '../../utils/eventPath';
export { cardGeometry, DEFAULT_RATIO } from './wallCardGeometry';

/**
 * An ALL EVENTS card (events-wall handoff §5). No border: a white surface on the page wash.
 *
 * The poster decides the layout. Its aspect ratio is read when it loads:
 *   portrait (< .95) on a card ≥ 480 wide → side by side, 44 / 56, image at clamp(r, .66, 1)
 *   portrait, narrower                    → stacked, same clamp
 *   landscape                             → image on top at clamp(r, 1.3, 2), cover
 * Until it loads the card assumes 1.5, the common flyer-banner shape.
 *
 * One CTA: PLEDGE → on magenta while a Party Starter is still funding, TICKETS → on black
 * otherwise (JOIN → when it's free, and the source's own page for an external listing). The
 * cover's top-right carries the share glyph and OPEN A PORTAL (CoverActions); `portal` is the
 * viewer's live Portal for this event, if they've opened one.
 *
 * Other surfaces (/events/all, the People feed) keep EventListItem — this is the wall's card.
 */
/** The picture on the card: the photo the host picked for the grid (default: the poster). */
export function cardImage(event) {
  return event?.cardImageUrl || event?.posterUrl || null;
}

/** Its framing — the card's own focal point (object-position), centred unless the host dragged it. */
export function cardFocal(event) {
  const n = (v) => { const x = Number(v); return Number.isFinite(x) ? Math.max(0, Math.min(100, x)) : 50; };
  return `${n(event?.cardFocalX)}% ${n(event?.cardFocalY)}%`;
}

/** Rough rendered height, for placing a card before it has been measured. */
export function estimateCardHeight(event, ratio, width) {
  const { side, aspect } = cardGeometry(ratio || event.posterAspectRatio, width);
  const starter = event.fundingType === 'party_starter';
  if (side) return Math.max((width * 0.44) / aspect, 300);
  return width / aspect + (starter ? 260 : 230);
}

function formatWhen(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
  return `${date} · ${time}`;
}

/**
 * The tag in the image's top-left corner (event-page handoff §3e): GUARANTEED · PARTY STARTER
 * (magenta) · LISTED · FREE · CURATED · NEEDS A HOST.
 */
export function cardTag(event) {
  const p = eventPricing(event);
  if (event?.isHostWanted) return { label: 'NEEDS A HOST', bg: '#B45309', fg: '#fff' };
  if (p.isExternalTicketing) return { label: 'LISTED', bg: '#6B7280', fg: '#fff' };
  if (event?.fundingType === 'party_starter') return { label: 'PARTY STARTER', bg: COLORS.starter, fg: '#fff' };
  if (p.isFree) return { label: 'FREE', bg: '#16A34A', fg: '#fff' };
  if (event?.mode === 'curated') return { label: 'CURATED', bg: '#000', fg: '#fff' };
  return { label: 'GUARANTEED', bg: '#000', fg: '#fff' };
}

/** The single money verb, shared with Featured so the two never disagree. */
export function eventCta(event) {
  const p = eventPricing(event, { decimals: 2 });
  const external = p.isExternalTicketing && event.externalTicketUrl;
  const label = event?.isHostWanted ? 'CLAIM →'
    : p.isExternalTicketing ? 'TICKETS ↗'
      : p.stillFunding ? 'PLEDGE →'
        : p.isFree ? 'RSVP →'
          : event?.mode === 'curated' ? 'APPLY →'
            : 'TICKETS →';
  return { ...p, label, external: !!external, magenta: p.stillFunding };
}

export function CtaButton({ event, onOpen, youreGoing = false }) {
  const base = eventCta(event);
  // Already holding a ticket (the People feed's YOU'RE GOING): the verb says so, and it opens the
  // event rather than selling another seat. An outside listing still goes to its own page.
  const cta = youreGoing && !base.external ? { ...base, label: 'YOU’RE IN →', magenta: false } : base;
  const cls = `flex h-10 flex-shrink-0 items-center whitespace-nowrap px-[18px] font-mono text-[12px] font-bold tracking-[0.18em] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${
    cta.magenta ? 'bg-[#FF00FF] text-white hover:bg-white hover:text-[#FF00FF]' : 'bg-black text-white hover:bg-white hover:text-black'
  }`;
  if (cta.external) {
    return (
      <a
        href={event.externalTicketUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => { e.stopPropagation(); recordExternalInterest(event.id); }}
        className={cls}
      >
        {cta.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }} className={cls}>
      {cta.label}
    </button>
  );
}

export function EventShareSheet({ event, open, onClose, originRect = null }) {
  if (!event) return null;
  return (
    <ShareSheet
      originRect={originRect}
      isOpen={open}
      onClose={onClose}
      title={event.title}
      venue={event.venueName}
      city={event.locationCity}
      url={`${webOrigin()}${eventPath(event)}`}
      inviteCode={event.inviteCode}
      date={event.dateTime}
      status={event.status}
      mode={event.mode}
      eventId={event.id}
      eventService={eventService}
      posterUrl={event.posterUrl}
    />
  );
}

/**
 * Extras for surfaces other than the wall:
 *   youreGoing     the viewer holds a ticket — the CTA reads YOU'RE IN →
 *   showAttendees  the avatar stack of who's in (the People feed shows who's going)
 * `width` and `ratio` are optional: a list that lays cards out (the wall's masonry) passes both;
 * anywhere else the card measures itself and reads its own poster.
 */
export default function WallEventCard({
  event, width, ratio, onRatio = null, onOpen = null, portal = null,
  youreGoing = false, showAttendees = false, loading = false,
  /** Review-step preview: extra props on the image box (a drag handler) and an object-position override. */
  imageProps = null, imagePosition = null,
}) {
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFrom, setShareFrom] = useState(null);
  const ref = useRef(null);
  const [ownWidth, setOwnWidth] = useState(600);
  const [ownRatio, setOwnRatio] = useState(null);
  useLayoutEffect(() => {
    if (width != null) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth) setOwnWidth(el.clientWidth); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  const w = width ?? ownWidth;
  const r = ratio ?? event.posterAspectRatio ?? ownRatio;
  const reportRatio = (id, value) => { if (ratio === undefined) setOwnRatio(value); onRatio?.(id, value); };
  const image = cardImage(event);
  const poster = usePosterReveal(image, value => reportRatio(event.id, value), event.posterUnavailable);
  const ready = poster.ready && !loading;
  const { side, aspect } = cardGeometry(r, w);
  const attendees = showAttendees && Array.isArray(event.attendees) ? event.attendees : [];

  const open = () => { if (onOpen) onOpen(event); else navigate(eventPath(event)); };
  const { stillFunding, priceText } = eventCta(event);
  const tag = cardTag(event);

  // Party Starter floor: RSVP-goal events count heads, cash ones count pledges.
  const isRsvp = event.goalType === 'rsvp';
  const goal = isRsvp ? (event.rsvpGoal || 0) : (event.goalCount || 0);
  const got = isRsvp ? (event.acceptedCount || 0) : (event.pledgedCount || 0);
  const pct = goal > 0 ? Math.min(100, Math.round((got / goal) * 100)) : 0;

  const place = [
    [event.locationCity, event.venueName].filter(Boolean).join(' · '),
    event.sponsorName ? `Presented by ${event.sponsorName}` : '',
  ].filter(Boolean).join(' · ').toUpperCase();

  return (
    <>
      <div className="wall-card-shell" data-ready={ready} aria-busy={!ready || undefined}>
      <WallCardSkeleton width={w} ratio={r} overlay />
      <article
        ref={ref}
        onClick={open}
        className="wall-card-content grid min-w-0 cursor-pointer bg-white"
        aria-hidden={!ready || undefined}
        inert={!ready}
        style={{ gridTemplateColumns: side ? 'minmax(0,44fr) minmax(0,56fr)' : 'minmax(0,1fr)' }}
        data-testid="wall-event-card"
        data-event-id={event.id}
        data-portal-to={eventPath(event)}
        data-layout={side ? 'side' : 'stacked'}
      >
        <div className="relative overflow-hidden" {...(imageProps || {})} style={{ background: '#0F0F0F', aspectRatio: String(aspect), ...(imageProps?.style || {}) }}>
          {image && !poster.failed ? (
            <img
              ref={poster.imageRef}
              src={image}
              alt=""
              loading="lazy"
              draggable={false}
              referrerPolicy="no-referrer"
              onLoad={poster.onLoad}
              onError={poster.onError}
              className="absolute inset-0 block h-full w-full object-cover"
              style={{ objectPosition: imagePosition || cardFocal(event), pointerEvents: imageProps ? 'none' : undefined, userSelect: 'none' }}
            />
          ) : (
            <PosterSlot posterUrl={null} alt={event.title} fallbackKey={event.id || event.inviteCode || event.title} className="absolute inset-0" />
          )}
          <span
            className="absolute left-0 top-0 font-mono text-[10px] font-bold tracking-[0.2em]"
            style={{ padding: '7px 11px', background: tag.bg, color: tag.fg }}
          >
            {tag.label}
          </span>
          {imageProps ? (
            <div style={{ pointerEvents: 'none' }}><CoverActions event={event} portal={portal} onShare={() => {}} /></div>
          ) : (
            <CoverActions event={event} portal={portal} onShare={(rect) => { setShareFrom(rect); setShareOpen(true); }} />
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-3" style={{ padding: side ? 24 : '22px 24px 24px' }}>
          <span data-pr-scramble className="font-mono text-[12px] font-semibold tracking-[0.16em] text-black">{formatWhen(event.dateTime)}</span>
          <h2
            data-pr-scramble
            className="m-0 font-mono font-medium normal-case tracking-normal text-black"
            style={{ fontSize: w > 520 ? 26 : 22, lineHeight: 1.2, textWrap: 'pretty' }}
          >
            {event.title}
          </h2>
          {place && <span className="font-mono text-[11px] tracking-[0.14em]" style={{ color: COLORS.muted }}>{place}</span>}

          {stillFunding && (
            <div className="mt-1 flex flex-col gap-1.5" data-testid="funding-bar">
              <div style={{ height: 4, background: COLORS.fundTrack }}>
                <div style={{ height: 4, width: `${pct}%`, background: COLORS.starter }} />
              </div>
              <span className="font-mono text-[10px] font-bold tracking-[0.16em] text-black">
                {got}/{goal} {isRsvp ? 'RSVP’D' : 'PLEDGED'} · {event.mode === 'curated' ? 'CURATED' : 'OPEN'}
              </span>
            </div>
          )}

          {attendees.length > 0 && (
            <AvatarStack
              people={attendees}
              max={3}
              label={<span><b>{got || event.acceptedCount || attendees.length}</b> {stillFunding ? 'pledged' : 'going'}</span>}
            />
          )}

          {/* The price takes what the CTA leaves and wraps anywhere: one unbreakable token
              here used to widen the row, push TICKETS past the card and give the whole wall a
              horizontal scroll. */}
          <div className="mt-auto flex min-w-0 items-center justify-between gap-3 pt-3.5">
            <span className="min-w-0 flex-1 font-mono text-[18px] font-bold uppercase text-black" style={{ overflowWrap: 'anywhere', fontSize: priceText.length > 12 ? 13 : 18 }}>{priceText}</span>
            <CtaButton event={event} onOpen={open} youreGoing={youreGoing} />
          </div>
        </div>
      </article>
      </div>
      <EventShareSheet event={event} open={shareOpen} originRect={shareFrom} onClose={() => setShareOpen(false)} />
    </>
  );
}
